import React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  OnChangeFn,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, Shield, Clock, Users } from 'lucide-react';
import { User } from '../types';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';

interface UserTableProps {
  users: User[];
  onUserSelect: (user: User, tab?: 'info' | 'compliance' | 'security') => void;
  totalCount: number;
  pageSize: number;
  pageIndex: number;
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
  onSortingChange: OnChangeFn<SortingState>;
  sorting: SortingState;
  loading: boolean;
}

const columnHelper = createColumnHelper<User>();

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onUserSelect,
  totalCount,
  pageSize,
  pageIndex,
  onPaginationChange,
  onSortingChange,
  sorting,
  loading,
}) => {
  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Full Name',
        cell: info => (
          <span className="text-sm text-gray-700 font-medium">
            {info.getValue() || info.row.original.email || '-'}
          </span>
        ),
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: 'Email Address',
        cell: info => (
          <span className="text-sm text-gray-600">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('organization', {
        id: 'organization',
        header: 'Organization',
        cell: info => (
          <span className="text-sm text-gray-600">
            {info.getValue() || '-'}
          </span>
        ),
      }),
      columnHelper.accessor('site', {
        id: 'site',
        header: 'Site',
        cell: info => (
          <span className="text-sm text-gray-600">
            {info.getValue() || '-'}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.handlerName || row.handlerUsername || row.handlerEmail || '', {
        id: 'handler',
        header: 'Manager',
        enableSorting: false,
        cell: info => {
          const user = info.row.original;
          const label = user.handlerName || user.handlerUsername || user.handlerEmail;
          if (!label) {
            return <span className="text-xs font-medium text-red-700">Missing manager</span>;
          }
          return <span className="text-sm text-gray-600">{label}</span>;
        },
      }),
   
      columnHelper.accessor('disabled', {
        id: 'disabled',
        header: 'Status',
        cell: info => (
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border bg-white ${
            info.getValue() ? 'border-red-200 text-red-800' : 'border-emerald-200 text-emerald-700'
          }`}>
            {info.getValue() ? 'Disabled' : 'Active'}
          </span>
        ),
      }),
      columnHelper.accessor('locked', {
        id: 'locked',
        header: 'Account Lock',
        cell: info => (
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border bg-white ${
            info.getValue() === 1 ? 'border-red-200 text-red-800' : 'border-emerald-200 text-emerald-700'
          }`}>
            {info.getValue() === 1 ? 'Locked' : 'Unlocked'}
          </span>
        ),
      }),
      columnHelper.accessor('compliant', {
        id: 'compliant',
        header: () => (
          <div className="flex items-center">
            <Shield className="h-4 w-4 mr-1" />
            <span>Compliance</span>
          </div>
        ),
        enableSorting: false,
        cell: info => {
          const user = info.row.original;
          const issues = Array.isArray(user.complianceIssues)
            ? user.complianceIssues.filter((issue) => `${issue || ''}`.trim().length > 0)
            : [];
          const isCompliant = info.getValue();
          const label = isCompliant
            ? 'Compliant'
            : (issues.length > 0 ? issues.join(', ') : 'Non-compliant');

          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUserSelect(user, 'compliance');
              }}
              className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label="Open compliance details"
              title={label}
            >
              <span
                className={cn(
                  'px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded border bg-white',
                  isCompliant ? 'border-emerald-200 text-emerald-700' : 'border-amber-200 text-amber-900'
                )}
              >
                {label}
              </span>
            </button>
          );
        },
      }),
      columnHelper.accessor('lastlogin', {
        id: 'lastlogin',
        header: () => (
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last Login</span>
          </div>
        ),
        cell: info => {
          const value = info.getValue();
          if (!value) return <span className="text-sm text-gray-500">Never</span>;
          const date = new Date(value);
          const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getFullYear()}`;
          return <span className="text-sm text-gray-500">{formattedDate}</span>;
        },
        sortingFn: (rowA, rowB) => {
          const aValue = rowA.original.lastlogin;
          const bValue = rowB.original.lastlogin;
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;
          const aDate = new Date(aValue).getTime();
          const bDate = new Date(bValue).getTime();
          return aDate - bDate;
        },
        enableSorting: true
      })
    ],
    [onUserSelect]
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: onSortingChange,
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const renderPagination = () => (
    <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-2 bg-gray-50 border-y border-gray-200">
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-1 mr-4">
          <button
            onClick={() => onPaginationChange(0, pageSize)}
            disabled={pageIndex === 0 || loading}
            className="px-2 py-1 text-xs border rounded bg-white disabled:opacity-50 hover:bg-gray-50 transition-colors"
            title="First Page"
          >
            {'<<'}
          </button>
          <button
            onClick={() => onPaginationChange(pageIndex - 1, pageSize)}
            disabled={pageIndex === 0 || loading}
            className="px-2 py-1 text-xs border rounded bg-white disabled:opacity-50 hover:bg-gray-50 transition-colors"
            title="Previous Page"
          >
            {'<'}
          </button>
          <button
            onClick={() => onPaginationChange(pageIndex + 1, pageSize)}
            disabled={pageIndex >= totalPages - 1 || loading}
            className="px-2 py-1 text-xs border rounded bg-white disabled:opacity-50 hover:bg-gray-50 transition-colors"
            title="Next Page"
          >
            {'>'}
          </button>
          <button
            onClick={() => onPaginationChange(totalPages - 1, pageSize)}
            disabled={pageIndex >= totalPages - 1 || loading}
            className="px-2 py-1 text-xs border rounded bg-white disabled:opacity-50 hover:bg-gray-50 transition-colors"
            title="Last Page"
          >
            {'>>'}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600">Show:</label>
          <select
            value={pageSize}
            onChange={e => {
              onPaginationChange(0, Number(e.target.value));
            }}
            disabled={loading}
            className="px-2 py-1 text-xs border rounded-md bg-white disabled:opacity-50 outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <span className="text-xs text-gray-600">
          Page <strong>{pageIndex + 1}</strong> of <strong>{totalPages}</strong>
          <span className="mx-2 text-gray-300">|</span>
          <strong>{totalCount}</strong> total users
        </span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {renderPagination()}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                      {header.column.getIsSorted() && (
                        header.column.getIsSorted() === 'asc' 
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-4 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12">
                  <EmptyState 
                    icon={Users}
                    title="No users found"
                    description="No users matched your search or filter criteria."
                  />
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => onUserSelect(row.original)}
                  className={cn(
                    'cursor-pointer',
                    row.original.disabled ? 'bg-gray-200 hover:bg-gray-300' : 'bg-white hover:bg-gray-50',
                    !row.original.compliant && !row.original.disabled ? 'bg-amber-50 hover:bg-amber-100' : '',
                    !row.original.compliant ? 'border-l-4 border-amber-500' : ''
                  )}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-4 py-2 whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default UserTable;
